import type { KpiPeriodBound } from '@klareospace/pharos-lib-types';
import { lastDayOfMonthUtc } from './date';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface KpiPeriod {
  from: KpiPeriodBound;
  to: KpiPeriodBound;
}

export interface KpiDateRange {
  fromDate: Date;
  toDateExclusive: Date;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Exclusive end boundary of a period bound — the UTC midnight that starts the day right after it. */
export function periodEnd(bound: KpiPeriodBound): Date {
  return new Date(Date.UTC(bound.year, bound.month - 1, (bound.day ?? 1) + 1));
}

/** Converts a `from`/`to` bound pair into an inclusive-start/exclusive-end UTC `Date` range. */
export function periodToDateRange(from: KpiPeriodBound, to: KpiPeriodBound): KpiDateRange {
  return {
    fromDate: new Date(Date.UTC(from.year, from.month - 1, from.day ?? 1)),
    toDateExclusive: periodEnd(to),
  };
}

/** Whether `date` falls within the (inclusive-start / exclusive-end) period described by `from`/`to`. */
export function isDateInPeriod(from: KpiPeriodBound, to: KpiPeriodBound, date: Date): boolean {
  const { fromDate, toDateExclusive } = periodToDateRange(from, to);
  return date.getTime() >= fromDate.getTime() && date.getTime() < toDateExclusive.getTime();
}

/** Today's UTC calendar day bound (given `now`) — shared BE/FE contract for "today" in KPI requests. */
export function toUtcDayBound(date: Date): Required<KpiPeriodBound> {
  return {
    day: date.getUTCDate(),
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear(),
  };
}

/** Inclusive mtd `to` bound: today (UTC), capped at the month's end; `undefined` if the month hasn't started yet. */
export function toMtdToBound(year: number, month: number, now: Date): KpiPeriodBound | undefined {
  const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const monthStart = new Date(Date.UTC(year, month - 1, 1));

  if (todayUtc.getTime() < monthStart.getTime()) {
    return undefined;
  }

  const monthEnd = new Date(Date.UTC(year, month - 1, lastDayOfMonthUtc(year, month)));
  const bound = todayUtc.getTime() < monthEnd.getTime() ? todayUtc : monthEnd;

  return toUtcDayBound(bound);
}
